import {
  KeyboardEvent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useDebounce } from 'use-debounce'
import { fetchQuery, useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { MINUTES } from '@sequencehq/utils/dates'
import {
  Banner,
  Box,
  Center,
  Flex,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IconMagnifyingGlass,
  IconXMark,
  Modal,
  Primary13Regular,
  ShadowL,
  Spinner,
  useModalClose,
  useNotifications
} from '@sequencehq/core-components'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { QuoteTemplateItem } from 'modules/Quotes/quoteTemplates/QuoteTemplateItem'

type QuoteTemplate = DashboardApi20240730.GetQuoteTemplate.Response

type QuoteTemplateListItem = {
  id: string
  title?: string
}

/** Fixed so switching between the grid and the empty state doesn't resize. */
const RESULTS_HEIGHT = '480px'
const GRID_COLUMNS = 4
const GRID_GAP = 16
const GRID_PADDING = 16
/** The strip card dimensions, set by QuoteTemplateItem. */
const CARD_WIDTH = 144
const CARD_HEIGHT = 224
const ROW_STRIDE = CARD_HEIGHT + GRID_GAP
/** Room for a classic (non-overlay) scrollbar next to the last column. */
const SCROLLBAR_ALLOWANCE = 16
const MODAL_WIDTH =
  GRID_COLUMNS * CARD_WIDTH +
  (GRID_COLUMNS - 1) * GRID_GAP +
  GRID_PADDING * 2 +
  SCROLLBAR_ALLOWANCE
/** Matches the templates strip: holds a skeleton over the preview, then fades. */
const PREVIEW_RENDER_DELAY = 150

/**
 * The one modal for applying a quote template — a searchable grid of the
 * Quotes-strip cards; clicking a card applies it. Used by the start pills, the
 * kebab menu, and the HubSpot deep-link.
 */
export const QuoteTemplatePickerModal = (props: {
  onClose: () => void
  onSelect: (template: QuoteTemplate) => Promise<void> | void
  hasSalesforceOpportunityId?: boolean
  showOverwriteWarning?: boolean
}) => {
  const { displayNotification } = useNotifications()
  const [searchTerm, setSearchTerm] = useState('')
  // Debounce the filter so keystrokes don't remount the heavy preview cards.
  const [debouncedSearch] = useDebounce(searchTerm, 150)
  const [applyingTemplateId, setApplyingTemplateId] = useState<string | null>(
    null
  )

  const templatesQuery = useQuery(
    dashboard20240730Client.getListQuoteTemplates,
    undefined,
    { select: res => res?.items ?? [], staleTime: 1 * MINUTES }
  )
  const templates = templatesQuery.data ?? []

  const filteredTemplates = useMemo(
    () =>
      templates.filter(template =>
        (template.title || 'Untitled Template')
          .toLowerCase()
          .includes(debouncedSearch.trim().toLowerCase())
      ),
    [templates, debouncedSearch]
  )

  const { onSelect, onClose } = props
  const selectTemplate = useCallback(
    async (id: string) => {
      if (applyingTemplateId) return
      setApplyingTemplateId(id)

      /** fetchQuery rejects on an API error, so failures land on `null` too. */
      const template = await fetchQuery(
        apiQueryClient,
        dashboard20240730Client.getQuoteTemplate,
        { id }
      ).catch(() => null)

      if (!template) {
        displayNotification('Failed to apply quote template', { type: 'error' })
        setApplyingTemplateId(null)
        return
      }

      try {
        await onSelect(template)
        onClose()
      } catch {
        displayNotification('Failed to apply quote template', { type: 'error' })
      } finally {
        setApplyingTemplateId(null)
      }
    },
    [applyingTemplateId, onSelect, onClose, displayNotification]
  )

  const showOverwriteWarning = props.showOverwriteWarning ?? true

  return (
    <Modal
      isOpen
      onClose={props.onClose}
      width={`${MODAL_WIDTH}px`}
      motionPreset="scale"
      overlayBackground="rgba(0,0,0,0.1)"
      overlayBlur
      boxShadow={`0 0 0 1px rgba(0,0,0,0.12), ${ShadowL}`}
      data-testid="quote.templatePicker.modal"
    >
      <Flex
        h="48px"
        align="center"
        gap="12px"
        px="16px"
        borderBottom={`1px solid ${GreyGrey30}`}
        flexShrink={0}
      >
        <IconMagnifyingGlass color={GreyGrey60} />
        <SearchInput
          autoFocus
          type="search"
          aria-label="Search templates"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search templates..."
          data-testid="quote.templatePicker.search"
        />
        <CloseButton />
      </Flex>
      {showOverwriteWarning && (
        <Box padding="16px 16px 0">
          <Banner
            variant="warning"
            data-testid="quote.templatePicker.overwriteWarning"
          >
            This will overwrite all data in the current quote, except the
            customer
            {props.hasSalesforceOpportunityId
              ? ' and Salesforce Opportunity ID, '
              : ', '}
            with the selected template.
          </Banner>
        </Box>
      )}
      <Box height={RESULTS_HEIGHT}>
        {templatesQuery.isPending ? (
          <Center height="100%">
            <Spinner />
          </Center>
        ) : filteredTemplates.length === 0 ? (
          <Center height="100%" data-testid="quote.templatePicker.noResults">
            <Box {...Primary13Regular} color={GreyGrey60}>
              No matching templates
            </Box>
          </Center>
        ) : (
          <TemplatePickerGrid
            templates={filteredTemplates}
            applyingTemplateId={applyingTemplateId}
            onSelectTemplate={selectTemplate}
          />
        )}
      </Box>
    </Modal>
  )
}

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${GreyGrey80};
  font-family: ${Primary13Regular.fontFamily};
  font-size: ${Primary13Regular.fontSize};
  font-weight: ${Primary13Regular.fontWeight};
  line-height: ${Primary13Regular.lineHeight};
  letter-spacing: ${Primary13Regular.letterSpacing};

  &::placeholder {
    color: ${GreyGrey60};
  }
`

/** Dismisses the modal through its animated close (Esc/overlay share this path). */
const CloseButton = () => {
  const requestClose = useModalClose()

  return (
    <Flex
      as="button"
      type="button"
      aria-label="Close"
      onClick={requestClose}
      justifyContent="center"
      alignItems="center"
      flexShrink={0}
      height="24px"
      width="24px"
      color={GreyGrey60}
      _hover={{ color: GreyGrey80 }}
      data-testid="quote.templatePicker.close"
    >
      <IconXMark />
    </Flex>
  )
}

/**
 * Memoized and row-virtualized so search keystrokes don't re-render or mount the
 * template cards — each renders a full, expensive quote preview.
 */
const TemplatePickerGrid = memo(
  (props: {
    templates: QuoteTemplateListItem[]
    applyingTemplateId: string | null
    onSelectTemplate: (id: string) => Promise<void>
  }) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const virtualizer = useVirtualizer({
      count: Math.ceil(props.templates.length / GRID_COLUMNS),
      getScrollElement: () => scrollRef.current,
      estimateSize: () => ROW_STRIDE,
      overscan: 1
    })

    const selectTemplate = (id: string) => {
      if (props.applyingTemplateId) return
      void props.onSelectTemplate(id)
    }

    const onCardKeyDown =
      (id: string) => (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          selectTemplate(id)
        }
      }

    return (
      <Box
        ref={scrollRef}
        height="100%"
        overflowY="auto"
        padding="16px"
        data-testid="quote.templatePicker.grid"
      >
        <Box
          position="relative"
          /** The last row contributes no trailing gap. */
          height={`${Math.max(virtualizer.getTotalSize() - GRID_GAP, 0)}px`}
        >
          {virtualizer.getVirtualItems().map(virtualRow => (
            <Flex
              key={virtualRow.key}
              position="absolute"
              top="0"
              left="0"
              gap={`${GRID_GAP}px`}
              transform={`translateY(${virtualRow.start}px)`}
            >
              {props.templates
                .slice(
                  virtualRow.index * GRID_COLUMNS,
                  virtualRow.index * GRID_COLUMNS + GRID_COLUMNS
                )
                .map(template => (
                  <Box
                    key={template.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={onCardKeyDown(template.id)}
                    opacity={
                      props.applyingTemplateId &&
                      props.applyingTemplateId !== template.id
                        ? 0.6
                        : 1
                    }
                    data-testid={`quote.templatePicker.card.${template.id}`}
                  >
                    <QuoteTemplateItem
                      quoteTemplateId={template.id}
                      delay={PREVIEW_RENDER_DELAY}
                      onSelect={() => selectTemplate(template.id)}
                      actionLabel="Use template"
                      inProgress={props.applyingTemplateId === template.id}
                      showMenu={false}
                    />
                  </Box>
                ))}
            </Flex>
          ))}
        </Box>
      </Box>
    )
  }
)
